import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, Observable, Observer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationConfigurationDto, ConfigStateService, PermissionService } from '@abp/ng.core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzAutosizeDirective } from 'source/app/shared/textarea-autosize-legacy/autosize.directive';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { AttachmentService } from '@proxy/common';
import { CreateAttachmentDto } from '@proxy/attachments';
import { XFilePreviewComponent } from 'source/app/xui/x-file-preview/x-file-preview.component';

import {
  MainGroupService,
  MainGroupVisitDto,
  CreateMainGroupVisitDto,
  UpdateMainGroupVisitDto,
  ProspectiveCustomerService,
  ProspectiveCustomerVisitDto,
} from 'source/app/proxy/customers';

@Component({
    selector: 'app-customer-group-visit',
    imports: [DatePipe, ReactiveFormsModule, FormsModule, NzUploadModule,
        NzFormModule, NzInputModule, NzAutosizeDirective, NzButtonModule, NzDatePickerModule, NzTableModule,
        NzTagModule, NzDividerModule, NzIconModule, NzGridModule, NzTooltipModule, NzPopconfirmModule],
    templateUrl: './customer-group-visit.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './customer-group-visit.component.scss'
})
export class CustomerGroupVisitComponent implements OnInit, OnChanges, OnDestroy {

  /** Optional: parent can still pass this in. If not provided,
   *  the component falls back to reading the 'id' segment from the parent route params. */
  @Input() mainGroupId: string | null = null;

  customerVisits: MainGroupVisitDto[] = [];
  visits: ProspectiveCustomerVisitDto[] = [];
  isLoadingVisits = false;
  visitsError: string | null = null;
  formGroup: FormGroup;
  attachmentList: any[] = [];
  currentUser: ApplicationConfigurationDto['currentUser'];

  editId: string | null = null;
  editingVisitId: string | null = null;
  editForm: Partial<UpdateMainGroupVisitDto> = {};
  canEditTabs = false;
  entityName = 'customer-visit';

  private destroy$ = new Subject<void>();
  private resolvedId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private mainGroupService: MainGroupService,
    private prospectiveCustomerService: ProspectiveCustomerService,
    private fb: FormBuilder,
    private attachmentService: AttachmentService,
    private config: ConfigStateService,
    private permission: PermissionService,
    private message: NzMessageService,
    private nzDrawerService: NzDrawerService
  ) {}

  ngOnInit(): void {
    this.config.getAll$().subscribe(config => {
      this.currentUser = config.currentUser;
      this.canEditTabs = this.permission.getGrantedPolicy('Masters.Customers.EditTabs');
      this.buildForm();
    });

    if (this.mainGroupId) {
      this.resolvedId = this.mainGroupId;
      this.loadCustomerVisits();
      this.loadVisits();
      return;
    }

    let r = this.route;
    while (r) {
      r.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
        if (params['id'] && !this.resolvedId) {
          this.resolvedId = params['id'];
          this.loadCustomerVisits();
          this.loadVisits();
        }
      });
      r = r.parent;
      if (!r) break;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mainGroupId']?.currentValue) {
      this.resolvedId = changes['mainGroupId'].currentValue;
      this.loadCustomerVisits();
      this.loadVisits();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm() {
    this.formGroup = this.fb.group({
      visitDate: [null, [Validators.required]],
      visitedBy: [this.currentUser?.id],
      visitMode: [null, [Validators.required]],
      comments: [null],
      nextFollowUpDate: [null],
      mainGroupId: [this.resolvedId],
    });
  }

  loadVisits() {
    if (!this.resolvedId) return;
    this.isLoadingVisits = true;
    this.visitsError = null;

    this.prospectiveCustomerService
      .getVisitsByConvertedMainGroupId(this.resolvedId)
      .subscribe({
        next: res => {
          this.visits = res?.visits ?? [];
          this.isLoadingVisits = false;
        },
        error: () => {
          this.visitsError = 'Could not load visit history.';
          this.visits = [];
          this.isLoadingVisits = false;
        }
      });
  }

  loadCustomerVisits() {
    if (!this.resolvedId) return;
    this.mainGroupService
      .getVisitsByMainGroupId(this.resolvedId)
      .subscribe(res => {
        this.customerVisits = res ?? [];
        this.customerVisits.forEach(visit => {
          (visit as any).attachments = [];
          this.attachmentService
            .getList({
              filter: `entityTypeFullName eq "${this.entityName}/attachment" && entityRecordId eq "${visit.id}"`,
              maxResultCount: 10,
              skipCount: 0,
            } as any)
            .subscribe(attachRes => {
              (visit as any).attachments = attachRes.items ?? [];
            });
        });
      });
  }

  create() {
    if (!this.canEditTabs) {
      this.message.error('No permission to add visit');
      return;
    }
    if (this.formGroup.invalid) return;

    const payload: CreateMainGroupVisitDto = {
      ...this.formGroup.value,
      visitDate: this.toDateOnly(this.formGroup.value.visitDate),
      nextFollowUpDate: this.toDateOnly(this.formGroup.value.nextFollowUpDate),
      mainGroupId: this.resolvedId,
      visitedBy: this.currentUser?.id,
      visitedByName: this.currentUser?.userName,
      active: true,
    };

    this.mainGroupService.createVisit(payload).subscribe(savedVisit => {
      if (this.attachmentList.length > 0) {
        this.uploadAttachment(savedVisit.id);
      } else {
        this.loadCustomerVisits();
      }
      this.buildForm();
      this.attachmentList = [];
      this.message.success('Visit added successfully!');
    });
  }

  delete(visitId: string) {
    if (!this.canEditTabs) {
      this.message.error('No permission to delete visit');
      return;
    }
    this.mainGroupService.deleteVisit(visitId).subscribe(() => {
      this.customerVisits = this.customerVisits.filter(v => v.id !== visitId);
      this.message.success('Visit deleted!');
    });
  }

  startEdit(visit: MainGroupVisitDto) {
    this.editId = visit.id;
    this.editingVisitId = visit.id;
    this.editForm = {
      visitDate: (visit.visitDate ? new Date(visit.visitDate) : null) as any,
      visitedBy: visit.visitedBy,
      visitedByName: visit.visitedByName,
      visitMode: visit.visitMode,
      comments: visit.comments,
      nextFollowUpDate: (visit.nextFollowUpDate ? new Date(visit.nextFollowUpDate) : null) as any,
      mainGroupId: visit.mainGroupId,
      active: visit.active,
      concurrencyStamp: visit.concurrencyStamp,
    };
  }

  cancelEdit() {
    this.editId = null;
    this.editForm = {};
  }

  saveEdit(visitId: string) {
    if (!this.canEditTabs) {
      this.message.error('No permission to edit visit');
      return;
    }
    if (!this.editForm.visitDate) {
      this.message.error('Visit Date is required!');
      return;
    }
    if (!this.editForm.visitMode || this.editForm.visitMode.trim() === '') {
      this.message.error('Visit Mode is required!');
      return;
    }
    const payload: UpdateMainGroupVisitDto = {
      ...(this.editForm as UpdateMainGroupVisitDto),
      visitDate: this.toDateOnly(this.editForm.visitDate),
      nextFollowUpDate: this.toDateOnly(this.editForm.nextFollowUpDate),
      mainGroupId: this.resolvedId,
      active: true,
    };
    this.mainGroupService.updateVisit(visitId, payload).subscribe(() => {
      this.editId = null;
      this.editForm = {};
      this.loadCustomerVisits();
      this.message.success('Visit updated successfully!');
    });
  }

  beforeUploadEdit = (file: File): boolean => {
    const isLt10M = (file as any).size / 1024 / 1024 < 10;
    if (!isLt10M) {
      this.message.error('File must be smaller than 10MB!');
      return false;
    }
    this.attachmentService
      .upload(`${this.entityName}/attachment`, this.editingVisitId, this.generateFormData(file) as any)
      .subscribe(path => {
        this.attachmentService.create({
          entityTypeFullName: `${this.entityName}/attachment`,
          isPublic: false,
          name: (file as any).name,
          path: path,
          size: (file as any).size,
          type: (file as any).type,
          entityRecordId: this.editingVisitId,
        } as CreateAttachmentDto).subscribe(() => {
          this.loadCustomerVisits();
          this.message.success('Attachment uploaded!');
        });
      });
    return false;
  };

  uploadAttachment(visitId: string) {
    this.attachmentList.forEach(file => {
      this.attachmentService.upload(
        `${this.entityName}/attachment`,
        visitId,
        this.generateFormData(file as any) as any
      ).subscribe({
        next: (path) => {
          this.attachmentService.create({
            entityTypeFullName: `${this.entityName}/attachment`,
            isPublic: false,
            name: (file as any).name,
            path: path,
            size: (file as any).size,
            type: (file as any).type,
            entityRecordId: visitId,
          } as CreateAttachmentDto).subscribe({
            next: () => {
              this.loadCustomerVisits();
            },
            error: () => {
              this.message.error('Failed to save attachment record.');
            }
          });
        },
        error: () => {
          this.message.error('Failed to upload attachment.');
        }
      });
    });
  }

  beforeUpload = (file: File) => {
    return new Observable((observer: Observer<boolean>) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        this.message.error('File must be smaller than 10MB!');
        observer.complete();
        return;
      }
      this.attachmentList = [...this.attachmentList, file as any];
      observer.next(false);
      observer.complete();
    });
  }

  generateFormData(file: File) {
    const fd = new FormData();
    fd.append('file', file);
    return fd;
  }

  deleteAttachment(att: any, visitId: string) {
    this.attachmentService.delete(att.id).subscribe(() => {
      const visit = this.customerVisits.find(v => v.id === visitId);
      if (visit) {
        (visit as any).attachments = (visit as any).attachments
          .filter((a: any) => a.id !== att.id);
      }
      this.message.success('Attachment deleted!');
    });
  }

  openPreview(att: any) {
    const fileType = att.type || '';
    if (fileType.includes('pdf') || fileType.includes('image')) {
      this.nzDrawerService.create({
        nzContent: XFilePreviewComponent,
        nzContentParams: { item: att },
      });
      return;
    }
    this.downloadFile(att);
  }

  downloadFile(att: any) {
    this.attachmentService.downloadById(att.id).subscribe((res: any) => {
      const base64 = res.content;
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: att.type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = att.name;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  }

  private toDateOnly(value: Date | string | null | undefined): string | null {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  disabledNextFollowUpDate = (current: Date): boolean => {
    const visitDate = this.formGroup?.get('visitDate')?.value;
    if (!visitDate) return false;
    const visit = new Date(visitDate); visit.setHours(0, 0, 0, 0);
    const cur = new Date(current); cur.setHours(0, 0, 0, 0);
    return cur < visit;
  };

  disabledNextFollowUpDateEdit = (current: Date): boolean => {
    if (!this.editForm?.visitDate) return false;
    const visit = new Date(this.editForm.visitDate);
    visit.setHours(0, 0, 0, 0);
    const cur = new Date(current);
    cur.setHours(0, 0, 0, 0);
    return cur < visit;
  };
}
